<template>
  <div class="py-5">
    <div v-if="!firstLoad" class="container is-fluid">
      <div class="level pt-0">
        <div class="level-left">
          <div class="mr-5">
            <label class="menu-label">RFQ Status</label>
            <div class="is-capitalized">
              {{ rfq.status.toLowerCase() }} 
            </div>
          </div>
          <div>
            <label class="menu-label">RFQ Type</label>
            <div class="is-capitalized">
              {{ rfq.rfq_type ? rfq.rfq_type : 'Full' }} 
            </div>
          </div>
        </div>
        <div class="level-right buttons">
          <button 
            @click="addComment()"
            class="button">
            <span class="icon">
              <i class="far fa-comment"></i>
            </span>
            <span>Leave a Comment</span>
          </button>
          <button 
            @click="confirmMarkComplete()"
            v-if="!rfq.is_completed && !rfq.needs_revision && can.markAsCompleted"
            class="button is-primary">
            Mark as Completed
          </button>
          <template v-if="rfq.is_completed && !rfq.is_accepted && can.markAsAccepted">
            <button
              @click="confirmRfqAccepted()" 
              class="button is-primary">
              Accept RFQ
            </button>
            <button
              @click="confirmRevisionsNeeded()"
              class="button is-warning">
              Request a Revision
            </button>
          </template>
          <template v-if="rfq.needs_revision && can.markAsCompleted">
            <button 
              @click="confirmRevisionsCompleted()"
              class="button is-primary">
              Mark Revisions as Completed
            </button>
          </template>
        </div>
      </div>

      <div class="columns is-multiline">
        <div class="column is-3">
          <label class="label">RFQ Name*</label>
          <v-text-field
            required
            placeholder="RFQ Name"
            v-model="rfq.rfq_name"
            :disabled="rfq.is_accepted"
            @blur="update('rfq_name')"/>
        </div>
        <div class="column is-3">
          <label class="label">Project*</label>
          <div class="mt-3">
            {{ rfq.project.project_name }}
          </div>
        </div>
        <div class="column is-6">
          <label class="label">Dropbox Link to Drawings*</label>
          <v-text-field
            required
            placeholder="Link to Architectural Drawings"
            v-model="rfq.drawing"
            @blur="update('drawing')"
            :disabled="rfq.is_accepted"
            :rules="[
              v => v.includes('http') ? true : 
                'Please add https:// in front of the url']"/>
        </div>
        <div class="column is-3">
          <label class="label">Sales Person*</label>
          <v-select
            v-if="users.length > 0"
            required
            :options="users.filter(d => ['sales', 'sales-estimator', 'sales-estimator-samples'].includes(d.role))"
            item-text="display_name"
            item-value="id"
            v-model="rfq.main_salesperson_id"
            :disabled="rfq.is_accepted"
            @input="update('main_salesperson_id')"
          />
        </div>
        <div class="column is-3">
          <label class="label">Estimator*</label>
          <v-select
            v-if="users.length > 0"
            required
            item-text="display_name"
            item-value="id"
            :options="users.filter(d => ['estimator', 'sales-estimator', 'sales-estimator-samples'].includes(d.role))"
            v-model="rfq.main_estimator_id"
            :disabled="rfq.is_accepted"
            @input="update('main_estimator_id')"
          />
        </div>
        <div class="column is-3">
          <label class="label">Quote Type*</label>
          <div v-if="rfq.rfq_type === CONDENSED">
            <v-select
              class="mt-1"
              :options="quoteTypes"
              v-model="rfq.quote_type"
              @input="update('quote_type')"
              :disabled="rfq.is_accepted"
              item-text="label"
              item-value="value"
              required
            />
          </div>
          <div class="flex flex--column" v-else>
            <v-checkbox 
              v-model="rfq.has_supply"
              @input="update('has_supply')" 
              :disabled="rfq.is_accepted"
              label="supply"
            />
            <v-checkbox 
               v-model="rfq.has_install" 
              @input="update('has_install')" 
              :disabled="rfq.is_accepted"
              label="install"
            />
          </div>
        </div>
        <div class="column is-3" v-if="rfq.rfq_type === CONDENSED">
          <label class="label">Stage*</label>
          <div>
            <v-select
              class="mt-1"
              :options="stages"
              required
              v-model="rfq.rfq_stage"
              @input="update('rfq_stage')"
              :disabled="rfq.is_accepted"
              item-text="label"
              item-value="value"
            />
          </div>
        </div>
        <div class="column is-3" v-if="rfq.rfq_type === CONDENSED">
          <label class="label">Purpose of Project*</label>
          <div>
            <v-text-field
              required
              class="mt-1"
              placeholder="Project Purpose"
              v-model="rfq.rfq_purpose"
              :disabled="rfq.is_accepted"
            />
          </div>
        </div>
        <div class="column is-3" v-if="rfq.rfq_type === CONDENSED">
          <label class="label">Material Onsite Date*</label>
          <div>
            <v-text-field 
              required
              class="mt-1"
              placeholder="Material Onsite Date (e.g. Q4 -2023)"
              v-model="rfq.rfq_material_onsite_date"
              :disabled="rfq.is_accepted"
            />
          </div>
        </div>
        <div class="column is-3" v-if="!isMitrex && rfq.rfq_type !== CONDENSED">
          <label class="label">Services*</label>
          <div class="flex flex--column">
            <v-checkbox 
              v-for="service in services" 
              :disabled="rfq.is_accepted"
              :key="service.id"
              :label="service.service_name"
              @input="updateService(service.id, $event)"
              :value="rfq.c_estimation ? rfq.c_estimation.services.map(
                d => d.service.id).includes(service.id) : false"
            />
          </div>
        </div>
        <div class="column is-full" v-if="filteredDocumentTypes.length > 0">
          <label class="label">Documents Required*</label>
          <div class="flex flex--row">
            <div 
              v-for="document in filteredDocumentTypes" 
              :key="document.id">
              <v-checkbox 
                class="mr-4"
                :disabled="rfq.is_accepted"
                :label="document.document_type_name"
                :value="rfq.documents.map(d => d.document_type_id).includes(document.id)"
                @input="toggleDocument(document.id, $event)"/>
              <v-datepicker-modal-slot
                v-if="rfq.documents.map(d => d.document_type_id).includes(document.id)"
                :value="parseDate(
                  rfq.documents.filter(
                    d => d.document_type_id == document.id)[0].received_on)"
                @input="updateDocumentDate(document.id, $event)">
                <a class="mt-1 ml-4 mr-2 help">
                  <span 
                    v-if="!rfq.documents.filter(
                    d => d.document_type_id == document.id)[0].received_on">
                    Select Date Received
                  </span>
                  <span v-else>
                    {{ formatDate(parseDate(rfq.documents.filter(d => d.document_type_id == document.id)[0].received_on)) }}
                  </span>
                </a>
              </v-datepicker-modal-slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="tabs mt-4">
      <ul>
        <li 
          @click="active = 'items'"
          :class="{ 'is-active': active == 'items' }">
          <a>Items</a>
        </li>
        <li 
          @click="active = 'history'"
          :class="{ 'is-active': active == 'history' }">
          <a>History</a>
        </li>        
        <li
          v-if="!firstLoad && rfq.drawing_highlights && rfq.drawing_highlights.length > 0"
          @click="active = 'drawing_highlights'"
          :class="{ 'is-active': active == 'drawing_highlights' }">
          <a>Drawing Highlights</a>
        </li>
        <li
          v-if="!firstLoad && rfq.c_estimation"
          @click="active = 'estimation'"
          :class="{ 'is-active': active == 'estimation' }">
          <a>Estimation</a>
        </li>
      </ul>
    </div>

    <div 
      v-if="!firstLoad" 
      v-show="active == 'items'"
      class="container is-fluid pt-3">

      <v-alert 
        v-if="!rfq.c_estimation || rfq.c_estimation.items.length == 0"
        type="info">
        There are no items in this rfq
      </v-alert>

      <template v-else>
        <div class="columns item-header">
          <div class="column label py-0">
            <label class="label">Item</label>
          </div>
          <div 
            class="column label py-0">
            <label class="label">Materials</label>
          </div>
          <div 
            class="column label py-0"
            v-if="rfq.rfq_type !== CONDENSED"
          >
            <label class="label">Fabrication</label>
          </div>
          <div 
            v-if="rfq.has_install"
            class="column label py-0">
            <label class="label">Installation</label>
          </div>
        </div>

        <div 
          v-for="(item, itemIndex) in rfq.c_estimation.items" 
          :key="item.id"
          class="columns is-multiline">
          <div class="column">
            <div class="item is-outlined is-relative">
              <div 
                v-if="!rfq.is_accepted"
                @click="confirmDeleteItem(item.id)" 
                class="delete">
              </div>
              <!-- <div>
                <label class="is-size-7">Name</label>
                <v-text-field 
                  required
                  class="mt-1"
                  placeholder="Name"
                  v-model="item.item_name"
                  @blur="updateItem({
                    itemId: item.id, 
                    data: {item_name:$event}
                  })"/>
              </div> -->
              <div>
                <label class="is-size-7">Architectural Name</label>
                <v-text-field 
                  required
                  class="mt-1"
                  placeholder="Architectural Name"
                  :disabled="rfq.is_accepted"
                  v-model="item.architecture_name"
                  @blur="$event && $event != '' ? updateItem({
                    itemId: item.id, 
                    data: { architecture_name: $event }
                  }) : setNotification({
                    status: true,
                    type: 'danger',
                    message: 'Architectural name cannot be empty'
                  })"/>
              </div>
            </div>
          </div>

          <div 
            class="column">

            <div
              v-for="(material, materialIndex) in item.materials"
              :key="`material-${materialIndex}`" 
              class="sub-item is-outlined mb-2">

              <product-selector 
                v-model="material.product_master_id"
                @input="updateMaterial(item.id, material, materialIndex)"
                show-category
                disable-category
                :product-object="material.product_master"
                :product-category-id="material.product_master.product_category_id"
                :product-category-object="material.product_master.product_category"
                :has-product-search="material.product_master.product_category_id == 13"
                :disabled="rfq.is_accepted"/>

              <div 
                v-if="!rfq.is_accepted"
                @click="material.id ? deleteItemMaterial(material.id) : 
                  remove({
                    section: 'materials', 
                    itemIndex: itemIndex,
                    subItemIndex: materialIndex
                  })" 
                class="delete">
              </div>
            </div>

            <div
              v-for="(processing, processingIndex) in item.processing"
              :key="`processing-${processingIndex}`"
              class="sub-item is-outlined mb-2">
              <div>
                <div class="menu-label mb-0">
                  {{ processing.processing.processing_type.processing_type_name }}
                </div>
                <v-select
                  class="mt-1"
                  is-small
                  v-model="processing.processing_id"
                  @input="updateProcessing(item.id, processing, processingIndex)"
                  :options="processingMethods.filter(
                    d => d.processing_type_id == processing.processing.processing_type.id)"
                  :disabled="rfq.is_accepted"
                  item-text="processing_name"
                  item-value="id"
                />
              </div>
              <div 
                v-if="!rfq.is_accepted"
                @click="processing.id ? deleteItemProcessing(processing.id) :
                  remove({
                    section: 'processing', 
                    itemIndex: itemIndex,
                    subItemIndex: processingIndex
                  })" 
                class="delete ml-2">
              </div>
            </div>

            <div 
              v-if="addingFinishing.includes(itemIndex) || item.finishing_name" 
              class="sub-item is-outlined mb-2"
            >
              <div>
                <div class="menu-label mb-0">
                  Finishing
                </div>
                <v-text-field 
                  placeholder="Enter finishing name"
                  v-model="item.finishing_name"
                  @blur="updateItem({
                    itemId: item.id, 
                    data: { finishing_name: $event }
                  })"
                  is-small
                  is-expanded
                  class="mt-1"
                  :disabled="rfq.is_accepted"
                />
              </div>
              <div 
                v-if="!rfq.is_accepted"
                @click="exitAddFinishing(item, itemIndex, true)" 
                class="delete ml-2">
              </div>
            </div>

            <div>
              <div
                v-if="!item.materials.map(
                  d => d.product_master.product_category_id).includes(14) &&
                  !item.materials.map(
                  d => d.product_master.product_category_id).includes(20) &&
                  !rfq.is_accepted &&
                  rfq.rfq_type !== CONDENSED"
                @click="addBacking(itemIndex)" 
                class="lg-add-btn is-size-7 mb-2">
                <span class="icon">
                  <i class="fa fa-plus"></i>
                </span>
                <span>Backing</span>
              </div>
              <div
                v-if="!item.materials.map(
                  d => d.product_master.product_category_id).includes(13) &&
                  !item.materials.map(
                  d => d.product_master.product_category_id).includes(20) &&
                  !rfq.is_accepted"
                @click="addFacing(itemIndex)"  
                class="lg-add-btn is-size-7 mb-2">
                <span class="icon">
                  <i class="fa fa-plus"></i>
                </span>
                <span>Facing</span>
              </div>
              <div
                v-if="!item.materials.map(
                  d => d.product_master.product_category_id).includes(20) && 
                  !item.materials.map(
                    d => d.product_master.product_category_id).includes(13) &&
                  !item.materials.map(
                    d => d.product_master.product_category_id).includes(14) &&
                  !rfq.is_accepted && isMitrex"
                @click="addRailing(itemIndex)"  
                class="lg-add-btn is-size-7 mb-2">
                <span class="icon">
                  <i class="fa fa-plus"></i>
                </span>
                <span>Railing</span>
              </div>
              <div
                v-if="!item.processing.map(
                  d => d.processing.processing_type.id).includes(1) &&
                  !rfq.is_accepted && rfq.rfq_type !== CONDENSED"
                @click="addProcessing({
                  itemIndex: itemIndex,
                  processingType: processingTypes.filter(d => d.id == 1)[0]
                })" 
                class="lg-add-btn is-size-7 mb-2">
                <span class="icon">
                  <i class="fa fa-plus"></i>
                </span>
                <span>Processing</span>
              </div>
              <div 
                v-if="((rfq.rfq_type !== CONDENSED && !item.processing.map(
                  d => d.processing.processing_type.id).includes(2)) || 
                  (rfq.rfq_type === CONDENSED && !addingFinishing.includes(itemIndex) && !item.finishing_name))
                  && !rfq.is_accepted"
                @click="rfq.rfq_type === CONDENSED
                  ? addingFinishing.push(itemIndex)
                  : addProcessing({
                    itemIndex: itemIndex,
                    processingType: processingTypes.filter(d => d.id == 2)[0]
                  })" 
                class="lg-add-btn is-size-7 mb-2">
                <span class="icon">
                  <i class="fa fa-plus"></i>
                </span>
                <span>Finishing</span>
              </div>
            </div>

          </div>

          <div class="column" v-if="rfq.rfq_type !== CONDENSED">
            <div
              v-for="(fabrication, fabricationIndex) in item.fabrications"
              :key="fabricationIndex" 
              class="sub-item is-outlined mb-2">
              <div>
                <div class="menu-label mb-0">
                  {{ fabrication.fabrication.fabrication_type.fabrication_type_name }}
                </div>
                <v-select
                  class="mt-1"
                  is-small
                  v-model="fabrication.fabrication_id"
                  @input="updateFabrication(item.id, fabrication, fabricationIndex)"
                  :options="fabrications.filter(
                    d => d.fabrication_type_id == fabrication.fabrication.fabrication_type.id)"
                  item-text="fabrication_name"
                  item-value="id"
                  :disabled="rfq.is_accepted"
                />
              </div>
              <div 
                v-if="!rfq.is_accepted"
                @click="fabrication.id ? deleteItemFabrication(fabrication.id) :
                  remove({
                    section: 'fabrications', 
                    itemIndex: itemIndex,
                    subItemIndex: faricationIndex
                  })" 
                class="delete ml-2">
              </div>
            </div>

            <article v-if="rfq.is_accepted && item.fabrications.length == 0" class="message">
              <div class="message-body">
                There are no fabrications for this item.
              </div>
            </article>

            <div v-if="!rfq.is_accepted">
              <template 
                v-for="fabricationType in fabricationTypes">
                <div
                  :key="`fabrication-type-${fabricationType.id}`" 
                  v-if="!item.fabrications.map(
                    d => d.fabrication.fabrication_type.id
                  ).includes(fabricationType.id)"
                  @click="addFabrication({ itemIndex, fabricationType })"
                  class="lg-add-btn is-size-7 mb-2">
                  <span class="icon">
                    <i class="fa fa-plus"></i>
                  </span>
                  <span>
                    {{ fabricationType.fabrication_type_name }}
                  </span>
                </div>
              </template>
            </div>
          </div>

          <div 
            v-if="installationTypes.length > 0 && rfq.has_install"
            class="column">
            <template
              v-for="(installation, installationIndex) in item.installations">
              <div
                v-if="[1,2].includes(installation.installation.installation_type.id)"
                :key="installationIndex" 
                class="sub-item is-outlined mb-2">
                <div>
                  <div class="menu-label mb-0">
                    {{ installation.installation.installation_type.installation_type_name }}
                  </div>
                  <v-select
                    class="mt-1"
                    is-small
                    v-if="installations.length > 0"
                    v-model="installation.installation_id"
                    @input="updateInstallation(item.id, installation, installationIndex)"
                    :options="installations.filter(
                      d => d.installation_type_id == installation.installation.installation_type.id)"
                    item-text="installation_name"
                    item-value="id"
                    :disabled="rfq.is_accepted"
                  />
                </div>
                <div 
                  v-if="!rfq.is_accepted"
                  @click="installation.id ? deleteItemInstallation(installation.id) :
                    remove({
                      section: 'installations', 
                      itemIndex: itemIndex,
                      subItemIndex: installationIndex
                    })" 
                  class="delete ml-2">
                </div>
              </div>
            </template>

            <div v-if="!rfq.is_accepted">
              <template 
                v-for="installationType in installationTypes.filter(
                  d => rfq.rfq_type !== CONDENSED ? [1,2].includes(d.id) : d.id === 1)"
              >
                <div
                  :key="`installation-type-${installationType.id}`" 
                  v-if="!item.installations.map(
                    d => d.installation.installation_type.id
                  ).includes(installationType.id)"
                  @click="addInstallation({ itemIndex, installationType })"
                  class="lg-add-btn is-size-7 mb-2">
                  <span class="icon">
                    <i class="fa fa-plus"></i>
                  </span>
                  <span>
                    {{ installationType.installation_type_name }}
                  </span>
                </div>
              </template>
            </div>

            <div v-if="rfq.rfq_type !== CONDENSED">
              <v-checkbox 
                class="is-size-7"
                :value="item.has_insulation" 
                @input="updateItem({
                  itemId: item.id,
                  data: { has_insulation: !item.has_insulation }
                })"
                :disabled="rfq.is_accepted"
                :label="installationTypes.filter(d => d.id == 3)[0].installation_type_name"/>

              <v-checkbox 
                class="is-size-7"
                v-for="installationType in installationTypes.filter(
                  d => ![1,2,3].includes(d.id))" 
                :key="`check-installation-${installationType.id}`"
                :value="item.installations.map(
                  d => d.installation.installation_type.id
                ).includes(installationType.id)" 
                @input="toggleInstallationType(itemIndex, installationType.id)"
                :disabled="rfq.is_accepted"
                :label="installationType.installation_type_name"/>
            </div>

          </div>

          <div 
            v-if="itemIndex != rfq.c_estimation.items.length-1" 
            class="column is-full py-0">
            <hr class="mt-0 mb-4">
          </div>

        </div>          
      </template>

      <div
        v-if="!rfq.is_accepted"
        class="columns">
        <div class="column is-full">
          <div
            @click="addingItem = true" 
            class="lg-add-btn spaced">
            <span class="icon">
              <i class="fa fa-plus"></i>
            </span>
            <span>Add an item</span>
          </div>
        </div>
      </div>

    </div>

    <div class="container is-fluid" v-if="active == 'history'">

      <v-alert 
        v-if="rfq.history.length == 0"
        type="info">
        There is no history for this RFQ.
      </v-alert>

      <table v-else class="table is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th>Update Date</th>
            <th>Updated By</th>
            <th>Change</th>
            <th>Comment</th>
          </tr>
        </thead>

        <tbody>
          <tr 
            v-for="update in rfq.history"
            :key="update.id">
            <td>
              {{ formatDatetime(parseDate(update.created_on)) }}
            </td>
            <td>
              {{ update.created_user ? update.created_user.display_name : '' }}
            </td>
            <td>
              {{ update.change }}
            </td>
            <td>
              <span v-html="update.comments"></span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="container is-fluid" v-if="active == 'drawing_highlights'">
      <router-link 
        class="button is-small is-link mb-3"
        :to="{ 
          name: 'DrawingHighlightViewer', 
          params: { drawingHighlightId: rfq.drawing_highlights[0].id }
        }">
        Go to Drawing Highlights
      </router-link>
      <drawing-highlights :drawing-highlight="rfq.drawing_highlights[0]"/>
    </div>

    <div class="container is-fluid" v-if="active == 'estimation'">
      <router-link 
        class="button is-small is-link mb-4"
        :to="{ 
          name: isMitrex ? 'EstimationViewerMitrex' : 'EstimationViewerCladify', 
          params: { estimationId: rfq.c_estimation.id }
        }">
        See Estimation
      </router-link>
      <div class="columns">
        <div class="card is-shadowless is-outlined mx-3 my-3">
          <header class="card-header">
            <p class="card-header-title">
              Estimation Status
            </p>
          </header>
          <div class="card-content px-4 py-3">
            <div class="content">
              {{ rfq.c_estimation.status }}
            </div>
          </div>
        </div>
        <div class="card is-shadowless is-outlined mx-3 my-3">
          <header class="card-header">
            <p class="card-header-title">
              Total Area
            </p>
          </header>
          <div class="card-content px-4 py-3">
            <div class="content">
              {{ rfq.c_estimation.total_area_sqft | commasep(0) }} SQFT
            </div>
          </div>
        </div>
        <div class="card is-shadowless is-outlined mx-3 my-3">
          <header class="card-header">
            <p class="card-header-title">
              Total Price Before Tax
            </p>
          </header>
          <div class="card-content px-4 py-3">
            <div class="content">
              {{ rfq.c_estimation.total_price_pre_tax | currency(0) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-confirm-modal 
      v-model="confirmDialog.state"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :type="confirmDialog.type"
      @confirm="confirmedCb ? confirmedCb() : null"
      @cancel="confirmDialog.state = false; cancelledCb ? cancelledCb() : null"
      :confirm-text="confirmDialog.confirmText"
      :show-cancel="confirmDialog.showCancel">
      <div v-if="confirmDialog.showComments">
        <v-rich-text class="has-text-left mt-2" v-model="comments"/>
      </div>
    </v-confirm-modal>

    <add-rfq-item-modal
      v-if="addingItem"
      v-model="addingItem"
      :rfq="rfq"/>

    <v-loading 
      key="RFQ"
      :value="firstLoad"
      message="Loading RFQ, this may take a few seconds ...">
    </v-loading>

  </div>
</template>

<script>
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'
import VDatepickerModalSlot from '@/components/base/VDatepickerModalSlot'
import VConfirmModal from '@/components/base/VConfirmModal'
import VAlert from '@/components/base/VAlert'
import VRichText from '@/components/base/VRichText'
import VLoading from '@/components/base/VLoading'

import DrawingHighlights from '@/components/estimations/DrawingHighlights'
import ProductSelector from '@/components/inventory/ProductSelector'
import AddRfqItemModal from '@/components/rfq/AddRfqItemModal'
import rfqCondensable from '@/mixins/rfqCondensable'
import { CONDENSED } from '@/helpers/constants'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseJSON, format } from 'date-fns'

export default {
  mixins: [rfqCondensable],
  components: {
    VSelect, VTextField, VCheckbox, VDatepickerModalSlot, VConfirmModal, VAlert, 
    VLoading, VRichText, ProductSelector, AddRfqItemModal, DrawingHighlights
  },
  data() {
    return {
      confirmDialog: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this item?',
        type: 'danger',
        showComments: false,
        showCancel: true,
        confirmText: 'Yes'
      },
      confirmedCb: null,
      cancelledCb: null,
      addingItem: false,
      active: 'items',
      formatDate: (date) => { return date ? format(date, "MM/dd/yyyy") : null },
      formatDatetime: (date) => { return date ? format(date, "MM/dd/yyyy H:mm:ss") : null },
      parseDate: (date) => { return date ? parseJSON(date) : date },
      comments: '',
      addingFinishing: []
    }
  },
  computed: {
    ...mapState("rfq", ["rfq", "loading", "firstLoad"]),
    ...mapState("users", ["users"]),
    ...mapState("auth", ["user"]),
    ...mapState("rfqDocumentTypes", ["documentTypes"]),
    ...mapState("services", ["services"]),
    ...mapState("processings", ["processingMethods", "processingTypes"]),
    ...mapState("fabrications", ["fabrications", "fabricationTypes"]),
    ...mapState("installations", ["installations", "installationTypes"]),
    ...mapState("auth", ["user"]),
    editable({ rfq, firstLoad }) {
      if (!firstLoad) {
        let highlight = rfq.drawing_highlights[0];
        return !highlight || highlight.is_complete == false;
      } else {
        return false
      }
    },
    isMitrex() {
      return this.rfq.product_brand == 'Mitrex'
    },
    can({ user }) {
      return {
        markAsCompleted: ["ceo", "sales", "estimator", "sales-estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin',
        markAsAccepted: ["ceo", "estimator", "sales-estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin'
      }
    },
    filteredDocumentTypes({ rfq, documentTypes }) {
      return documentTypes.filter(t => 
        rfq.rfq_type === CONDENSED 
          ? t.is_for_condensed_proposal 
          : t.is_for_full_proposal
      )
    }
  },
  created() {
    this.initialize();
  },
  beforeDestroy() {
    this.setFirstLoad(true);
  },
  methods: {
    ...mapActions("rfq", [
      "getRfq", "updateRfq", "addDocument", "updateDocument", "deleteDocument", 
      "addService", "deleteService", "addItem", "deleteItem", "updateItem", 
      "addItemMaterial", "updateItemMaterial", "deleteItemMaterial", 
      "addItemProcessing", "updateItemProcessing", "deleteItemProcessing", 
      "addItemFabrication", "updateItemFabrication", "deleteItemFabrication", 
      "addItemInstallation", "updateItemInstallation", "deleteItemInstallation"
    ]),
    ...mapMutations("rfq", ["setLoading", "setFirstLoad", "setRfq"]),
    ...mapMutations("rfq", [
      "addBacking", "addFacing", "addRailing", "addProcessing", "addFabrication", 
      "addInstallation", "remove"
    ]),
    ...mapMutations("app", ["setNotification"]),
    ...mapActions("users", ["getUsers"]),
    ...mapActions("services", ["getServices"]),
    ...mapActions("processings", ["getProcessingMethods", "getProcessingTypes"]),
    ...mapActions("rfqDocumentTypes", ["getDocumentTypes"]),
    ...mapActions("fabrications", ["getFabrications", "getFabricationTypes"]),
    ...mapActions("installations", ["getInstallations", "getInstallationTypes"]),
    initialize() {
      let rfq = this.$route.params.rfq;
      
      if (rfq) {
        this.setRfq(rfq);
        this.setLoading(false);
        this.setFirstLoad(false);
      } else {
        this.getRfq(this.$route.params.rfqId);
      }

      this.getUsers({ per_page: 100, is_active: true });

      if (this.services.length == 0) {
        this.getServices({ per_page: 100 });
      }

      if (this.processingMethods.length == 0) {
        this.getProcessingMethods({ per_page: 100 });
      }

      if (this.processingTypes.length == 0) {
        this.getProcessingTypes();
      }

      if (this.documentTypes.length == 0) {
        this.getDocumentTypes();
      }

      if (this.fabrications.length == 0) {
        this.getFabrications({ per_page: 100 });
      }

      if (this.fabricationTypes.length == 0) {
        this.getFabricationTypes();
      }

      if (this.installations.length == 0) {
        this.getInstallations({ per_page: 100 });
      }

      if (this.installationTypes.length == 0) {
        this.getInstallationTypes();
      }
    },
    toggleInstallationType(itemIndex, installationTypeId) {
      let item = this.rfq.c_estimation.items[itemIndex];

      let index = item.installations.map(
        d => d.installation.installation_type.id
      ).indexOf(installationTypeId);

      let installation = this.installations.filter(
        d => d.installation_type.id == installationTypeId
      )[0];

      if (index == -1) {
        this.addItemInstallation({
          data:{
            estimation_item_id: item.id,
            installation_id: installation.id
          }
        })
      } else {
        this.deleteItemInstallation(item.installations[index].id);
      }
    },
    removeInstallation() {
      this.rfq.c_estimation.items.forEach(item => {
        item.installations.forEach(installation => {
          this.deleteItemInstallation(installation.id);
        })
        if (item.has_insulation) {
          this.updateItem({
            itemId: item.id,
            data: { has_insulation: false }
          })
        }
      })
    },
    update(field) {
      let data = {};
      const value = this.rfq[field]
      data[field] = value;

      
      if (field === 'quote_type') {
        const quoteTypeSelected = this.quoteTypes.find(t => t.value === value)
        const hasSupply = quoteTypeSelected.has_supply
        const hasInstall = quoteTypeSelected.has_install
        this.rfq.has_supply = hasSupply
        this.rfq.has_install = hasInstall
        data['has_supply'] = hasSupply
        data['has_install'] = hasInstall
        if (!hasInstall) {
          this.removeInstallation();
        }
      }

      this.updateRfq({
        rfqId: this.rfq.id,
        data: data
      })

      if (field === 'has_install' && value === false) {
        this.removeInstallation()
      }
    },
    updateService(serviceId, v) {
      if (v) {
        this.addService({
          estimation_id: this.rfq.c_estimation.id,
          service_id: serviceId
        });
      } else {
        let id = this.rfq.c_estimation.services.filter(
          d => d.service_id == serviceId
        )[0].id
        
        this.deleteService(id)
      }
    },
    toggleDocument(documentTypeId, v) {
      if (v) {
        this.addDocument({ 
          project_rfq_id: this.rfq.id,
          document_type_id: documentTypeId 
        });
      } else {
        let documentId = this.rfq.documents.filter(
          d => d.document_type_id == documentTypeId
        )[0].id;
        this.deleteDocument(documentId);
      }
    },
    updateDocumentDate(documentTypeId, v) {
      let document = {};
      this.rfq.documents.forEach(d => {
        if (d.document_type_id == documentTypeId) {
          d.received_on = v;
        }
        document = d;
      });
      this.updateDocument({
        documentId: document.id,
        data: { received_on: v }
      });
    },
    updateMaterial(itemId, material, ind) {
      if (!material.id) {
        this.addItemMaterial({
          data: {
            estimation_item_id: itemId,
            product_master_id: material.product_master_id
          }, 
          replaceIndex: ind 
        })
      } else {
        this.updateItemMaterial({
          itemMaterialId: material.id,
          data: { product_master_id: material.product_master_id }
        })
      }
    },
    updateProcessing(itemId, processing, ind) {
      if (!processing.id) {
        this.addItemProcessing({
          data: {
            estimation_item_id: itemId,
            processing_id: processing.processing_id
          },
          replaceIndex: ind
        })
      } else {
        this.updateItemProcessing({
          itemProcessingId: processing.id,
          data: { processing_id: processing.processing_id }
        })
      }
    },
    updateFabrication(itemId, fabrication, ind) {
      if (!fabrication.id) {
        this.addItemFabrication({
          data: {
            estimation_item_id: itemId,
            fabrication_id: fabrication.fabrication_id
          },
          replaceIndex: ind
        })
      } else {
        this.updateItemFabrication({
          itemFabricationId: fabrication.id,
          data: { fabrication_id: fabrication.fabrication_id }
        })
      }
    },
    updateInstallation(itemId, installation, ind) {
      if (!installation.id) {
        this.addItemInstallation({
          data: {
            estimation_item_id: itemId,
            installation_id: installation.installation_id
          },
          replaceIndex: ind
        })
      } else {
        this.updateItemInstallation({
          itemInstallationId: installation.id,
          data: { installation_id: installation.installation_id }
        })
      }
    },
    confirmDeleteItem(itemId) {
      this.confirmedCb = () => {
        this.deleteItem(itemId);
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Delete Confirmation';
      this.confirmDialog.type = 'danger';
      this.confirmDialog.message = 'Are you sure you want to delete this item?';
      this.confirmDialog.state = true;
      this.confirmDialog.showComments = false;
    },
    confirmMarkComplete() {
      this.confirmedCb = () => {
        this.rfq.is_completed = true;
        this.update('is_completed');
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Confirm Mark as Complete';
      this.confirmDialog.type = 'warning';
      this.confirmDialog.message = 'Are you sure you are done creating this RFQ? \
        An email will be sent out to the estimator for review';
      this.confirmDialog.state = true;
      this.confirmDialog.showComments = false;
    },
    confirmRevisionsCompleted() {
      this.confirmedCb = () => {
        this.rfq.is_completed = true;
        this.update('is_completed');
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Confirm Revisions Completed';
      this.confirmDialog.type = 'warning';
      this.confirmDialog.message = 'Are you sure you are done revising this RFQ? \
        An email will be sent out to the estimator for review';
      this.confirmDialog.state = true;
      this.confirmDialog.showComments = false;
    },
    confirmRevisionsNeeded() {
      this.confirmedCb = () => {
        this.updateRfq({
          rfqId: this.rfq.id,
          data: {
            needs_revision: true,
            comments: this.comments
          }
        })
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Confirm Revisions Needed';
      this.confirmDialog.type = 'warning';
      this.confirmDialog.message = 'Please leave notes for why a revision is needed';
      this.confirmDialog.state = true;
      this.confirmDialog.showComments = true;
    },
    confirmRfqAccepted() {
      this.confirmedCb = () => {
        this.rfq.is_accepted = true;
        this.update('is_accepted');
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Confirm RFQ Accepted';
      this.confirmDialog.type = 'warning';
      this.confirmDialog.message = 'Are you sure you want to accept this RFQ? \
        No further changes may be made to the RFQ after it is accepted.';
      this.confirmDialog.state = true;
      this.confirmDialog.showComments = false;
    },
    addComment() {
      this.confirmedCb = () => {
        this.updateRfq({
          rfqId: this.rfq.id,
          data: {
            comments: this.comments 
          }
        }).then(() => {
          this.setNotification({
            status: true,
            type: 'success',
            message: 'RFQ comment successfully added.'
          })
          this.comments = '';
        })
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Add a comment'
      this.confirmDialog.type = 'info';
      this.confirmDialog.message = 'Please add your comment below';
      this.confirmDialog.state = true;
      this.confirmDialog.showComments = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/flexbox.scss';

.grouped-buttons {
  display: flex;
  flex-direction: row;
  margin: 0em -5px;
}

.lg-add-btn {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  background: rgb(240, 240, 240);
  padding: 0.5em;
  border-radius: 4px;
  &:hover {
    background: #eee;
  }
  &.grouped {
    width: 25%;
    margin: 0px 5px;
  }
  &.spaced {
    padding: 1em;
  }
}

.is-outlined {
  border: 1px solid gainsboro;
  border-radius: 4px;
}

.item {
  border-radius: 4px;
  padding: 0.75em;
  flex-grow: 1;

  .delete {
    position: absolute !important;
    top: 5px;
    right: 5px;
  }
}

.sub-item {
  border-radius: 4px;
  padding: 0.5em;
  margin-bottom: 0.5em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.item-header {
  position: sticky;
  top: 0px;
  background: #fff;
  z-index: 5;
}
</style>